import { DefaultFormContainer, FlexDiv, FormInputErrorMessage, FormItemTitle, MapFormContainer } from "src/components/application/formComponents/styles";
import { RenewVerifyItemFormProps } from "./types";
import StepBullet from "src/components/basic/StepBullet";
import { LeftBar } from "src/components/basic/LeftBar";
import StandardInput from "src/components/basic/StandardInput";
import { ChangeEvent, useState } from "react";
import { validEmailPattern } from 'src/constants/general';
import { isInValidHours } from 'src/utils/formDataManipulation';
import StandardSelect from "src/components/basic/StandardSelect";
import { aboutAttorneyPosition, states } from "src/constants/application";
import { ValidationErrorMessage } from "src/components/basic/ValidationErrorMessage/ValidationErrorMessage";
import { OptionButtonsContainer, RenewVerifyItemContainer } from "src/components/application/defaultComponents";
import OptionButton from "src/components/basic/OptionButton";
import BarCardNumberInput from "src/components/basic/BarCardNumberInput";
import StandardFormatInput from "src/components/basic/StandardFormatInput";
import { AttorneyData } from "src/dtos/applicationData";

export const RenewVerifyItemForm = ({
    attorney,
    setAttorney,
    attorneyNumber,
    isIncorrect,
    errorCodes = []
}: RenewVerifyItemFormProps) => {
    const [isEmailValid, setIsEmailValid] = useState(false);

    const handleEmailBlur = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        let isValid = validEmailPattern.test(event.target.value);
        setIsEmailValid(isValid)
    }
    
    const handleBarCardNumberChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setAttorney({ [name]: value })
      }

    const handleLicenseRelatedInputs = (changes: Partial<AttorneyData['license']>) => {
        setAttorney({ license: { ...attorney?.license, ...changes } })
    }

    return (
        <MapFormContainer>
            <DefaultFormContainer>
                <FlexDiv>
                    <StepBullet id={`att__${attorneyNumber + 1}`}>{attorneyNumber + 1}</StepBullet>
                    <FlexDiv dir="column" maxWidth='460px'>
                        <FormItemTitle>{attorney.firstName} {attorney.lastName}</FormItemTitle>
                        {
                            <RenewVerifyItemContainer>
                                <LeftBar />
                                <div className='form-container'>
                                    <StandardInput
                                        label='First Name'
                                        value={attorney.firstName}
                                        onChange={e => setAttorney({ firstName: e.target.value })}
                                        isIncorrect={isIncorrect && !attorney.firstName}
                                        errorMessage={isIncorrect && !attorney.firstName ? 'This is a required field' : ''}
                                        errorCode={errorCodes[0]}
                                    />
                                    <StandardInput
                                        label='Last Name'
                                        value={attorney.lastName}
                                        onChange={e => setAttorney({ lastName: e.target.value })}
                                        isIncorrect={isIncorrect && !attorney.lastName}
                                        errorMessage={isIncorrect && !attorney.lastName ? 'This is a required field' : ''}
                                        errorCode={errorCodes[1]}
                                    />
                                    <StandardInput
                                        label='Email'
                                        type='email'
                                        name="emailAddress"
                                        value={attorney?.emailAddress ?? ''}
                                        onChange={e => setAttorney({ emailAddress: e.target.value })}
                                        onBlur={handleEmailBlur}
                                        isIncorrect={isIncorrect && !attorney?.emailAddress && isEmailValid}
                                        errorMessage={isIncorrect && !isEmailValid ? 'Please input a valid email address' : ''}
                                        errorCode={errorCodes[4]}
                                    />
                                    <StandardInput
                                        label={<span>Average hours worked per week for this firm (Not Just Billable Hours)</span>}
                                        type="number"
                                        value={String(attorney?.workHistory?.hoursPerWeek ?? '')}
                                        onChange={e => setAttorney({ workHistory: { hoursPerWeek: (isNaN(Number(e.target.value)) || !e.target.value) ? undefined : Number(e.target.value) } })}
                                        //onBlur={handleAverageHourDateBlur}
                                        isIncorrect={isIncorrect && isInValidHours(attorney?.workHistory?.hoursPerWeek)}
                                        errorMessage={isIncorrect && isInValidHours(attorney?.workHistory?.hoursPerWeek) ? 'Must be between 0 and 99' : ''}
                                        min={0}
                                        max={99}
                                        errorCode={errorCodes[13]}
                                    />
                                    <StandardSelect
                                        label='Position'
                                        name="position"
                                        value={attorney?.position ?? ''}
                                        onChange={e => setAttorney({ position: e.target.value })}
                                        isIncorrect={isIncorrect && !attorney?.position}
                                        errorMessage={isIncorrect && !attorney?.position ? 'This is a required field' : ''}
                                        errorCode={errorCodes[3]}
                                    >
                                        <option disabled value="">Select</option>
                                        {aboutAttorneyPosition.map((position, index) => (
                                            <option key={index} value={position}>{position}</option>
                                        ))}
                                    </StandardSelect>
                                    <OptionButtonsContainer>
                                        <span>Is this attorney currently licensed in Texas?</span>
                                        <div>
                                            <OptionButton
                                                checked={attorney?.license?.isLicensedInTexas === true}
                                                onChange={() => handleLicenseRelatedInputs({ isLicensedInTexas: true })}
                                                isIncorrect={isIncorrect && attorney?.license?.isLicensedInTexas === undefined}
                                                errorCode={errorCodes[19]}
                                            >
                                                Yes
                                            </OptionButton>
                                            <OptionButton
                                                checked={attorney?.license?.isLicensedInTexas === false}
                                                onChange={() => handleLicenseRelatedInputs({ isLicensedInTexas: false })}
                                                isIncorrect={isIncorrect && attorney?.license?.isLicensedInTexas === undefined}
                                                errorCode={errorCodes[19]}
                                            >
                                                No
                                            </OptionButton>
                                            {isIncorrect && attorney?.license?.isLicensedInTexas === undefined ? (
                                                <FormInputErrorMessage>
                                                    This is a required field
                                                </FormInputErrorMessage>
                                            ) : null}
                                        </div>
                                    </OptionButtonsContainer>
                                    {!!attorney?.license?.isLicensedInTexas && (
                                        <>
                                            <BarCardNumberInput
                                                name='barCardNumber'
                                                label='Texas Bar Card Number'
                                                onChange={handleBarCardNumberChange}
                                                value={attorney?.barCardNumber ?? ''}
                                                errorCode={errorCodes[2]}
                                            />
                                            <StandardFormatInput
                                                label='Date licensed in Texas'
                                                maskStyle='date'
                                                value={attorney?.license?.licenseDate ?? ''}
                                                leftIcon="calendar"
                                                onValueChange={({ formattedValue }) => handleLicenseRelatedInputs({licenseDate: formattedValue})}
                                                isIncorrect={isIncorrect}
                                                errorCode={errorCodes[6]}
                                            />
                                        </>
                                    )}
                                    <OptionButtonsContainer>
                                        <span>Is this attorney currently licensed in a state other than Texas?</span>
                                        <div>
                                            <OptionButton
                                                checked={attorney?.license?.isLicensedInOtherState === true}
                                                onChange={() => handleLicenseRelatedInputs({ isLicensedInOtherState: true })}
                                                isIncorrect={isIncorrect && attorney?.license?.isLicensedInOtherState === undefined}
                                                errorCode={errorCodes[19]}
                                            >
                                                Yes
                                            </OptionButton>
                                            <OptionButton
                                                checked={attorney?.license?.isLicensedInOtherState === false}
                                                onChange={() => handleLicenseRelatedInputs({ isLicensedInOtherState: false })}
                                                isIncorrect={isIncorrect && attorney?.license?.isLicensedInOtherState === undefined}
                                                errorCode={errorCodes[19]}
                                            >
                                                No
                                            </OptionButton>
                                        </div>
                                        <ValidationErrorMessage errorCode={errorCodes[23]} />
                                    </OptionButtonsContainer>

                                    {!!attorney?.license?.isLicensedInOtherState && (
                                        <>
                                            <BarCardNumberInput
                                                name="outSideTexasBarCardNumber"
                                                label='Other State Bar Card Number'
                                                isOutsideTexas={true}
                                                onChange={handleBarCardNumberChange}
                                                value={attorney?.outSideTexasBarCardNumber ?? ''}
                                                errorCode={errorCodes[18]}
                                            />
                                            <StandardSelect
                                                label='State'
                                                name="state"
                                                value={attorney?.license?.otherState ?? ''}
                                                onChange={e => handleLicenseRelatedInputs({ otherState: e.target.value })}
                                                //onBlur={handleOtherStateBlur}
                                                isIncorrect={!attorney?.license?.otherState}
                                                errorMessage={!attorney?.license?.otherState ? 'This is a required field' : ''}
                                                errorCode={errorCodes[8]}
                                            >
                                                <option disabled value="">Select</option>
                                                {states.map((position, index) => (
                                                    <option key={index} value={position.abbreviation}>{position.name}</option>
                                                ))}
                                            </StandardSelect>

                                            <StandardFormatInput
                                                label='Date licensed in selected State'
                                                labelSize='high'
                                                maskStyle='date'
                                                leftIcon="calendar"
                                                value={attorney?.license?.dateLicensedInOtherState ?? ''}
                                                onValueChange={({ formattedValue }) => handleLicenseRelatedInputs({ dateLicensedInOtherState: formattedValue })}
                                                errorCode={errorCodes[9]}
                                            />

                                            <StandardInput
                                                label={
                                                    <>
                                                        Percentage of time "<u>CURRENTLY</u>" practicing in this jurisdiction'
                                                    </>
                                                }
                                                value={attorney?.license?.percentageOfWorkSpent}
                                                onChange={e => handleLicenseRelatedInputs({ percentageOfWorkSpent: e.target.value })}
                                                isIncorrect={!attorney?.license?.percentageOfWorkSpent}
                                                errorMessage={!attorney?.license?.percentageOfWorkSpent ? 'This is a required field' : ''}
                                                errorCode={errorCodes[10]}
                                            />
                                            <FlexDiv gap="24px">
                                                <LeftBar />
                                                <FlexDiv>

                                                    <StandardInput
                                                        label="If licensed to practice law in any additional states or jurisdictions than the ones reported above, please provide the other date(s) licensed and whether these license(s) are still active. If any are currently active, please provide the % of your work spent in each jurisdiction."
                                                        rows={4}
                                                        value={attorney?.license?.licensesInfo ?? ''}
                                                        onChange={(e) => handleLicenseRelatedInputs({ licensesInfo: e.target.value })}
                                                        isIncorrect={isIncorrect && !attorney?.license?.licensesInfo}
                                                        errorMessage={isIncorrect && !attorney?.license?.licensesInfo ? 'This is a required field' : ''}
                                                        placeholder=""
                                                        errorCode={errorCodes[11]}
                                                    />

                                                </FlexDiv>
                                            </FlexDiv>
                                        </>
                                    )}
                                    <ValidationErrorMessage errorCode={errorCodes[19]} />
                                </div>
                            </RenewVerifyItemContainer>
                        }
                    </FlexDiv>
                </FlexDiv>
            </DefaultFormContainer>
        </MapFormContainer>
    );

}
